import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { useTheme } from '@mui/material/styles';
// react-input-mask was abandoned in 2020 and only loaded under React 19 via
// loose peer deps; replaced with the actively-maintained react-imask. The
// mask alphabet differs: `9` (any digit) becomes `0`. See PhoneMaskedInput
// and BirthDateMaskedInput below.
import { IMaskInput } from 'react-imask';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import styled from '@emotion/styled';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import { handleVacancyApplication, getUploadUrl } from 'actions/vacancy';
import { addMessage } from 'actions/error';
import Message from 'components/Snackbars/Message';
import Alert from '@mui/material/Alert';
import { MAX_SIZE } from 'variables/main';

const StyledField = styled(TextField)(({ theme }) => ({
  '& textarea': {
    lineHeight: '24px'
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
    padding: 0
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: 8,
  '& .MuiSelect-select': {
    padding: '8px 14px'
  }
}));

const Form = styled('form')(() => ({
  maxWidth: 800,
  margin: '0 auto'
}));

const ButtonSubmitStyled = styled(Button)(({ theme }) => ({
  fontSize: 16,
  borderRadius: 8,
  backgroundColor: theme.palette.primary.salad,
  color: theme.palette.grey[600],
  padding: '14px 29px',
  width: '100%',
  margin: '18px 0',
  '&:hover': { backgroundColor: theme.palette.primary.main }
}));

const FlexRow = styled('div')(({ mb }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: mb || 20,
  gap: 10,
  '& > div': {
    flex: 1
  }
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.grey[600],
  marginBottom: 5,
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
    lineHeight: '24px'
  }
}));

const UploadCVButton = styled(Button)(({ theme }) => ({
  fontSize: 16,
  borderBottom: `1px solid ${theme.palette.black.main}`,
  borderRadius: 8,
  color: theme.palette.grey[600],
  padding: 0,
  fontWeight: 400,
  textTransform: 'none',
  marginBottom: 5
}));

const FormControlLabelStyled = styled(FormControlLabel)(() => ({
  '& .MuiFormControlLabel-label': {
    fontSize: 16
  }
}));

const InputField = ({
  name,
  label,
  placeholder,
  multiline = false,
  rows = 1,
  formData,
  setFormData,
  errors,
  type
}) => {
  if (type === 'phone' || type === 'birthDate') {
    const mask = type === 'phone' ? '+380 (00) 000-00-00' : '00.00.0000';
    return (
      <div>
        <FieldLabel>{label}</FieldLabel>
        <StyledField
          name={name}
          placeholder={placeholder}
          fullWidth
          error={errors.includes(name)}
          value={formData[name] || ''}
          slotProps={{
            input: {
              // MUI 9 routes inputComponent through slotProps.input. We
              // hand-wire react-imask: the masked component owns the DOM
              // <input> and reports completed/partial values via onAccept,
              // which we surface as a synthetic change event to keep the
              // existing setFormData call path unchanged.
              inputComponent: React.forwardRef(function MaskedInput(props, ref) {
                const { onChange, ...other } = props;
                return (
                  <IMaskInput
                    {...other}
                    mask={mask}
                    inputRef={ref}
                    onAccept={(value) =>
                      onChange?.({ target: { name: other.name, value } })
                    }
                  />
                );
              })
            }
          }}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, [name]: e.target.value }))
          }
        />
      </div>
    );
  }

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <StyledField
        name={name}
        placeholder={placeholder}
        fullWidth
        multiline={multiline}
        rows={rows}
        value={formData[name] || ''}
        error={errors.includes(name)}
        onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))}
      />
    </div>
  );
};

const SelectField = ({ name, label, options, formData, setFormData, errors }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <FormControl fullWidth error={errors.includes(name)}>
      <StyledSelect
        value={formData[name] || ''}
        onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))}
        displayEmpty>
        <MenuItem value="" disabled>
          {label}
        </MenuItem>
        {options.map(({ label, value }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  </div>
);

const CheckboxField = ({ name, label, formData, setFormData }) => (
  <FormGroup>
    <FormControlLabelStyled
      control={
        <Checkbox
          checked={formData[name] || false}
          onChange={() => setFormData((prev) => ({ ...prev, [name]: !prev[name] }))}
        />
      }
      label={label}
    />
  </FormGroup>
);

const SubmitForm = ({ vacancy, children }) => {
  const dispatch = useDispatch();
  const t = useTranslate('Vacancy');
  const theme = useTheme();
  const [errors, setErrors] = React.useState([]);
  const [formData, setFormData] = React.useState({});
  const [fileName, setFileName] = React.useState(null);
  const [successShown, setSuccessShown] = React.useState(false);

  const requiredFields = React.useMemo(() => ['name', 'surname', 'email', 'phone'], []);

  const validateForm = React.useCallback(() => {
    const newErrors = requiredFields.filter((field) => !formData[field]?.trim());

    const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const isPhoneValid = (phone) => /^\+380 \(\d{2}\) \d{3}-\d{2}-\d{2}$/.test(phone);

    if (formData.email && !isEmailValid(formData.email)) {
      newErrors.push('email');
    }

    if (formData.phone && !isPhoneValid(formData.phone)) {
      newErrors.push('phone');
    }

    setErrors(newErrors);

    return newErrors.length === 0;
  }, [formData, requiredFields]);

  const handleFileUploadAction = React.useCallback(
    async (event) => {
      const file = event.target.files[0];

      if (file) {
        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'text/plain',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ];

        if (!allowedTypes.includes(file.type)) {
          return;
        }

        if (file.size > MAX_SIZE) {
          return;
        }

        const result = await getUploadUrl(vacancy._id, file.type)(dispatch);

        const { key, url: uploadUrl } = result;

        try {
          await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type
            },
            body: file
          });

          setFileName(file.name);

          setFormData((prev) => ({
            ...prev,
            resumeFileKey: key
          }));
        } catch (err) {
          dispatch(addMessage(new Message('FileUploadError', 'error')));
        }
      }
    },
    [dispatch, vacancy._id]
  );

  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      const normalizePhone = (phone) => {
        return phone.replace(/\D/g, '').replace(/^380/, '80');
      };

      formData.phone = normalizePhone(formData.phone);

      const result = await dispatch(handleVacancyApplication(vacancy._id, formData));

      if (!result || result instanceof Error) {
        return;
      }

      setFormData({});

      setFileName(null);

      setErrors([]);

      setSuccessShown(true);

      setTimeout(() => setSuccessShown(false), 6000);
    },
    [dispatch, vacancy._id, formData, validateForm]
  );

  return (
    <Form onSubmit={handleSubmit}>
      {successShown ? (
        <Alert
          severity="success"
          icon={false}
          onClose={() => setSuccessShown(false)}
          style={{ marginBottom: 20 }}>
          {t('VacancyApplySuccess')}
        </Alert>
      ) : (
        <>
          <FlexRow>
            <InputField
              name="name"
              label={t('userName')}
              placeholder={t('userName')}
              {...{ formData, setFormData, errors }}
            />
            <InputField
              name="surname"
              label={t('surName')}
              placeholder={t('surName')}
              {...{ formData, setFormData, errors }}
            />
          </FlexRow>

          <FlexRow>
            <InputField
              name="birthDate"
              type="birthDate"
              label={t('birthDate')}
              placeholder={t('birthDate')}
              {...{ formData, setFormData, errors }}
            />
            <SelectField
              name="gender"
              label={t('gender')}
              options={[
                { label: t('male'), value: 'male' },
                { label: t('female'), value: 'female' }
              ]}
              {...{ formData, setFormData, errors }}
            />
          </FlexRow>

          <FlexRow>
            <InputField
              name="workPlace"
              label={t('workPlace')}
              placeholder={t('workPlacePlaceholder')}
              {...{ formData, setFormData, errors }}
            />
            <SelectField
              name="educationLevel"
              label={t('educationLevel')}
              options={[
                { label: t('highSchool'), value: 'highSchool' },
                { label: t('profTech'), value: 'profTech' },
                { label: t('highEducationIncomplete'), value: 'highEducationIncomplete' },
                { label: t('highEducation'), value: 'highEducation' }
              ]}
              {...{ formData, setFormData, errors }}
            />
          </FlexRow>

          <FlexRow mb={'0px'}>
            <InputField
              name="email"
              label={t('email')}
              placeholder={t('email')}
              {...{ formData, setFormData, errors }}
            />
            <InputField
              name="phone"
              type="phone"
              label={t('phone')}
              placeholder={t('phone')}
              {...{ formData, setFormData, errors }}
            />
          </FlexRow>

          <FlexRow>
            <CheckboxField
              name="experienced"
              label={t('experienced')}
              {...{ formData, setFormData }}
            />
            <CheckboxField
              name="wantToWorkPartTime"
              label={t('wantToWorkPartTime')}
              {...{ formData, setFormData }}
            />
          </FlexRow>

          <FlexRow>
            <InputField
              name="additionInfo"
              label={t('additionInfo')}
              placeholder={t('additionInfoPlaceholder')}
              multiline
              rows={4}
              {...{ formData, setFormData, errors }}
            />
          </FlexRow>

          <FlexRow>
            <div>
              <FieldLabel>{t('CVTitle')}</FieldLabel>
              <UploadCVButton
                component="label"
                style={
                  errors.includes('resumeFileKey')
                    ? { color: theme.palette.error.main, borderColor: theme.palette.error.main }
                    : {}
                }>
                {fileName || t('CVPlaceholder')}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={handleFileUploadAction}
                />
              </UploadCVButton>
              {errors.includes('resumeFileKey') && (
                <Typography variant="caption" color={theme.palette.error.main} component={'p'}>
                  {t('CVRequired')}
                </Typography>
              )}
            </div>
          </FlexRow>

          <ButtonSubmitStyled type="submit">{t('SendCV')}</ButtonSubmitStyled>

          {children}
        </>
      )}
    </Form>
  );
};

export default SubmitForm;
